import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import {DollarSign, Edit, Plus, Search, Tag, Trash2, TrendingUp, Users} from "lucide-react"

import Pagination from "@/components/pagination.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  TaxModuleFiltersType,
  TaxModuleType,
  PaginationType
} from "./types.ts"
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import { taxModuleIconByName } from "./icons.ts";

interface IndexProps {
  tax_modules: TaxModuleType[]
  filters: TaxModuleFiltersType
  pagination: PaginationType
  can_destroy?: boolean
}

export default function Index({ tax_modules, filters, pagination, can_destroy = false }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/tax_modules", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (taxModule: TaxModuleType) => {
    if (!window.confirm(`Supprimer le module fiscal "${taxModule.name}" ?`)) return

    router.delete(`/admin/tax_modules/${taxModule.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Modules fiscaux" />

      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Les modules fiscaux</h1>
            <p className="text-sm text-muted-foreground">Gestion des modules disponibles dans l'application</p>
          </div>
          <Link href="/admin/tax_modules/new">
            <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" />
              Ajouter un module
            </button>
          </Link>
        </div>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total catégories</p>
                <p className="mt-2 text-3xl font-bold text-foreground">0</p>
              </div>
              <Tag className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total des contribuables</p>
                <p className="mt-2 text-3xl font-bold text-foreground">0</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total des collectes</p>
                <p className="mt-2 text-3xl font-bold text-success">0 FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Moyenne par contribuable</p>
                <p className="mt-2 text-3xl font-bold text-warning">0 FCFA</p>
              </div>
              <TrendingUp className="h-8 w-8 text-warning" />
            </div>
          </div>
        </div>


        <Card className="border-border bg-card p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  placeholder="Rechercher un module fiscal par nom, code ou description..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-3">
            {tax_modules.map((tax) => {
              const Icon = taxModuleIconByName(tax.icon)

              return (
                <div
                  key={tax.id}
                  className="cursor-pointer rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50"
                  role="button"
                  tabIndex={0}
                  onClick={() => router.visit(`/admin/tax_modules/${tax.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      router.visit(`/admin/tax_modules/${tax.id}`)
                    }
                  }}
                >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="rounded-md bg-primary/10 p-2 text-primary">
                            <Icon className="h-4 w-4" />
                          </span>
                          <p className="text-lg font-semibold text-foreground">{tax.name}</p>
                          <span className={`rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary`}>
                        {tax.code}
                      </span>
                          {tax.active && (
                              <span className={`rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success`}>
                          Actif
                        </span>
                          )}
                        </div>

                        <p className="mt-2 text-sm text-muted-foreground">0</p>

                        <div className="mt-3 grid gap-4 md:grid-cols-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Couleur</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">{tax.color}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Code</p>
                            <p className="mt-1 text-sm font-medium text-foreground">{tax.code}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Total collecté</p>
                            <p className="mt-1 text-sm font-semibold text-success">
                              0 FCFA
                            </p>
                          </div>

                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="gap-2" aria-label={`Voir ${tax.name}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {can_destroy && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            aria-label={`Supprimer ${tax.name}`}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              handleDestroy(tax)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
              )
            })}
          </div>
        </Card>

        <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            basePath="/admin/tax_modules"
            query={query}
        />
      </div>

    </>
  )
}
